import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Typography } from '@material-ui/core';
import Icon from '@mdi/react';
import { mdiBellOutline, mdiPhoneMissed, mdiVideoOutline } from '@mdi/js';

import Button from 'components/Button/Button';
import {
  dismissIncomingCall,
  dismissMissedCalls,
  getChatControlData,
  markIncomingCallMissed,
  setChatDialogOpen,
  setVideoChatOpen,
} from 'components/ChatDialog/store/slices/chatSlice';
import { getVideoChatControlData } from 'components/ChatDialog/store/slices/videoChatSlice';
import { setThreadWithUsers } from 'components/ChatDialog/store/thunks/loadThreadUsers';
import { loadVideoChatUsers } from 'components/ChatDialog/store/thunks/loadVideoChatUsers';
import { getAttendees } from 'store/videoChat/services/getAttendees.service';
import { declineCall } from 'store/videoChat/services/declineCall.service';
import {
  closeCallNotification,
  requestNotificationPermission,
  showMissedCallNotification,
} from 'utils/browserNotifications';
import { getNotificationPermission } from 'utils/notificationPrompt';
import { isPushActive, syncPushSubscription } from 'utils/pushSubscription';
import { startRingtone, stopRingtone } from 'utils/ringtone';

// A call nobody has answered by now counts as missed, as the push rings for.
export const RING_FOR_MS = 30000;

// More ringing at once is beyond what anyone could answer; the oldest wait.
const RINGING_SHOWN = 3;

const useStyles = makeStyles((theme) => ({
  stack: {
    position: 'fixed',
    top: theme.spacing(2),
    left: '50%',
    transform: 'translateX(-50%)',
    width: 360,
    maxWidth: `calc(100vw - ${theme.spacing(4)}px)`,
    zIndex: theme.zIndex.snackbar + 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      top: theme.spacing(1),
    },
  },
  card: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  avatar: {
    width: 40,
    height: 40,
    flex: 'none',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },
  missedIcon: {
    color: theme.palette.error.main,
    flex: 'none',
  },
  title: {
    ...theme.typography.fontPanton,
    fontSize: '1rem',
    lineHeight: 1.3,
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    opacity: 0.75,
    fontSize: '0.8rem',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    '& > *': {
      flex: 1,
    },
  },
  // The shared Button has no success colour; its own text is white when not
  // outlined, so only the fill is set here.
  answer: {
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  missedRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  missedName: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  close: {
    marginTop: theme.spacing(1.5),
    width: '100%',
  },
  offer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: '0.8rem',
    opacity: 0.85,
  },
  offerIcon: {
    flex: 'none',
    marginTop: 1,
  },
  link: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    // The text's own colour: the theme's secondary all but vanishes on the
    // classic theme's dark paper.
    color: 'inherit',
    fontWeight: 600,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

const initialsOf = (user) =>
  `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() ||
  '?';

const formatElapsed = (ms) => {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const useCallerName = (callerId) => {
  const { t } = useTranslation('common');
  const { users } = useSelector(getVideoChatControlData);
  const caller = users[callerId];
  return {
    caller,
    name:
      [caller?.firstName, caller?.lastName].filter(Boolean).join(' ') ||
      t('chat.unknown_caller', 'Someone'),
  };
};

/** Open a call's thread with the video view: answering, or calling back. */
const useOpenCall = () => {
  const dispatch = useDispatch();
  const store = useStore();
  return (threadId) => {
    const thread = getChatControlData(store.getState()).threads[threadId];
    if (!thread) return;
    dispatch(setThreadWithUsers({ thread }));
    dispatch(setChatDialogOpen(true));
    dispatch(setVideoChatOpen(true));
  };
};

/**
 * Offers browser notifications wherever a call can reach someone without
 * them: always, unless they are already allowed. A browser that has blocked
 * them cannot be asked again from the page, so it only says where to look.
 */
const NotificationOffer = () => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [permission, setPermission] = useState(getNotificationPermission);

  if (permission === 'granted' || permission === 'unsupported') {
    return null;
  }

  const enable = async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === 'granted') {
      syncPushSubscription();
    }
  };

  return (
    <div className={classes.offer}>
      <Icon path={mdiBellOutline} size={0.7} className={classes.offerIcon} />
      {permission === 'denied' ? (
        <span>
          {t(
            'chat.calls_blocked_hint',
            'Notifications are blocked in your browser settings, so calls reach you only while Lixani is open.'
          )}
        </span>
      ) : (
        <span>
          {t(
            'chat.calls_when_closed_hint',
            'Get calls even when Lixani is closed.'
          )}{' '}
          <button type="button" className={classes.link} onClick={enable}>
            {t('chat.enable_notifications', 'Turn on notifications')}
          </button>
        </span>
      )}
    </div>
  );
};

/**
 * One ringing call, with its own clock: after RING_FOR_MS it checks whether
 * the call was answered somewhere this tab did not hear about, and otherwise
 * counts it missed.
 */
const RingingCallCard = ({ call, withOffer }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const store = useStore();
  const { t } = useTranslation('common');
  const openCall = useOpenCall();
  const { caller, name } = useCallerName(call.callerId);
  const [shownAt] = useState(Date.now);
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const timeout = setTimeout(async () => {
      const current = getChatControlData(store.getState()).incomingCalls.find(
        (each) => each.sessionId === call.sessionId
      );
      if (current?.status !== 'ringing') return;
      const thread = getChatControlData(store.getState()).threads[
        call.threadId
      ];
      try {
        const attendees = thread ? await getAttendees(thread) : [];
        if (
          (attendees || []).some(
            (attendee) => attendee.userId && attendee.userId !== call.callerId
          )
        ) {
          dispatch(dismissIncomingCall(call.sessionId));
          return;
        }
      } catch (error) {
        // No session any more, or no answer: treat it as unanswered.
      }
      dispatch(markIncomingCallMissed(call.sessionId));
    }, RING_FOR_MS);

    return () => {
      clearInterval(tick);
      clearTimeout(timeout);
    };
  }, [call.sessionId]);

  const answer = () => {
    dispatch(dismissIncomingCall(call.sessionId));
    openCall(call.threadId);
  };

  // Tells the caller this user can't answer now, and stops the call ringing
  // this user's other browsers. Not waited for: the card goes either way.
  const notNow = () => {
    dispatch(dismissIncomingCall(call.sessionId));
    const thread = getChatControlData(store.getState()).threads[call.threadId];
    if (thread) {
      declineCall(thread, call.sessionId).catch(() => {});
    }
  };

  return (
    <Paper className={classes.card} elevation={8} role="alert">
      <div className={classes.header}>
        <div className={classes.avatar}>{initialsOf(caller)}</div>
        <div>
          <Typography className={classes.title}>
            {`${name} ${t('chat.is_calling', 'is calling')}`}
          </Typography>
          <Typography component="div" className={classes.subtitle}>
            <Icon path={mdiVideoOutline} size={0.6} />
            {formatElapsed(now - shownAt)}
          </Typography>
        </div>
      </div>
      <div className={classes.actions}>
        <Button className={classes.answer} onClick={answer}>
          {t('chat.answer_call', 'Answer')}
        </Button>
        <Button outline onClick={notNow}>
          {t('chat.not_now', 'Not now')}
        </Button>
      </div>
      {withOffer && <NotificationOffer />}
    </Paper>
  );
};

const MissedCallerRow = ({ callerId, count, threadId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const openCall = useOpenCall();
  const { name } = useCallerName(callerId);

  const callBack = () => {
    dispatch(dismissMissedCalls(callerId));
    openCall(threadId);
  };

  return (
    <div className={classes.missedRow}>
      <Typography className={classes.missedName}>
        {count > 1 ? `${name} (${count})` : name}
      </Typography>
      <Button outline size="small" onClick={callBack}>
        {t('chat.call_back', 'Call back')}
      </Button>
    </div>
  );
};

/** Every missed call in one card, a row per caller, newest caller first. */
const MissedCallsCard = ({ calls, withOffer }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const byCaller = [];
  [...calls].reverse().forEach((call) => {
    const row = byCaller.find((each) => each.callerId === call.callerId);
    if (row) {
      row.count += 1;
    } else {
      // Newest first, so a caller's row calls back their latest thread.
      byCaller.push({
        callerId: call.callerId,
        threadId: call.threadId,
        count: 1,
      });
    }
  });

  return (
    <Paper className={classes.card} elevation={8} role="status">
      <div className={classes.header}>
        <Icon path={mdiPhoneMissed} size={1} className={classes.missedIcon} />
        <Typography className={classes.title}>
          {calls.length > 1
            ? t('chat.missed_calls', 'Missed calls')
            : t('chat.missed_call', 'Missed call')}
        </Typography>
      </div>
      {byCaller.map((row) => (
        <MissedCallerRow key={row.callerId} {...row} />
      ))}
      <Button
        outline
        className={classes.close}
        onClick={() => dispatch(dismissMissedCalls())}
      >
        {t('common.close', 'Close')}
      </Button>
      {withOffer && <NotificationOffer />}
    </Paper>
  );
};

/**
 * The in-app incoming-call banner: 1:1 calls reaching this user show here,
 * on any page of any open Lixani tab, with or without browser notifications.
 * Each ringing call has a card of its own, newest on top; a call goes away
 * when answered (here or elsewhere), and becomes missed when the caller hangs
 * up or nobody answers within RING_FOR_MS. Missed calls gather in one card
 * below, a row per caller. The notifications offer rides on the top card.
 * While anything rings, one tab plays the ringtone (utils/ringtone).
 *
 * Mounted once in App, next to the notification hooks. The calls live in
 * chatSlice (`incomingCalls`), fed by the chat socket's call frames.
 */
const IncomingCallBanner = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { incomingCalls } = useSelector(getChatControlData);
  const { users } = useSelector(getVideoChatControlData);

  // Callers' names may not be loaded yet.
  const unknownCallers = Array.from(
    new Set(
      incomingCalls
        .map((call) => call.callerId)
        .filter((callerId) => callerId && !users[callerId])
    )
  );
  const unknownKey = unknownCallers.join(',');
  useEffect(() => {
    if (unknownCallers.length > 0) {
      dispatch(loadVideoChatUsers(unknownCallers));
    }
  }, [unknownKey]);

  const ringing = incomingCalls
    .filter((call) => call.status === 'ringing')
    .reverse()
    .slice(0, RINGING_SHOWN);
  const missed = incomingCalls.filter((call) => call.status === 'missed');

  // The chime loops while any call rings, and stops the moment none does:
  // answered, not now, hung up or missed alike.
  const isRinging = ringing.length > 0;
  useEffect(() => {
    if (isRinging) {
      startRingtone();
    } else {
      stopRingtone();
    }
  }, [isRinging]);
  useEffect(() => stopRingtone, []);

  // Keep this device's system notification in step with the banner. A call
  // that leaves the list (answered here or elsewhere, opened, not now, or a
  // missed call dealt with) takes its notification with it; a call that turns
  // missed turns it into "Missed call", which with push on the server sends
  // to every device instead.
  const previousCalls = useRef(new Map());
  useEffect(() => {
    const current = new Map(
      incomingCalls.map((call) => [call.sessionId, call])
    );
    previousCalls.current.forEach((status, sessionId) => {
      const call = current.get(sessionId);
      if (!call) {
        closeCallNotification(sessionId);
      } else if (
        status === 'ringing' &&
        call.status === 'missed' &&
        !isPushActive()
      ) {
        const caller = users[call.callerId];
        const name =
          [caller?.firstName, caller?.lastName].filter(Boolean).join(' ') ||
          t('chat.unknown_caller', 'Someone');
        showMissedCallNotification(
          sessionId,
          `${t('chat.missed_call', 'Missed call')}: ${name}`,
          call.threadId
        );
      }
    });
    previousCalls.current = new Map(
      incomingCalls.map((call) => [call.sessionId, call.status])
    );
  }, [incomingCalls]);

  if (ringing.length === 0 && missed.length === 0) return null;

  return (
    <div className={classes.stack}>
      {ringing.map((call, index) => (
        <RingingCallCard
          key={call.sessionId}
          call={call}
          withOffer={index === 0}
        />
      ))}
      {missed.length > 0 && (
        <MissedCallsCard calls={missed} withOffer={ringing.length === 0} />
      )}
    </div>
  );
};

export default IncomingCallBanner;
